// Source: https://github.com/DavidHDev/react-bits/tree/main/src/ts-tailwind/TextAnimations/BlurText (MIT + Commons Clause)
import { motion, type Transition } from 'framer-motion';
import { useEffect, useRef, useState, type FC } from 'react';

type BlurTextProps = {
  text?: string;
  delay?: number;
  className?: string;
  animateBy?: 'words' | 'letters';
  threshold?: number;
  stepDuration?: number;
};

const FROM = { filter: 'blur(10px)', opacity: 0, y: -50 };
const KEYFRAMES = {
  filter: ['blur(10px)', 'blur(5px)', 'blur(0px)'],
  opacity: [0, 0.5, 1],
  y: [-50, 5, 0]
};
const TIMES = [0, 0.5, 1];

const BlurText: FC<BlurTextProps> = ({
  text = '',
  delay = 200,
  className = '',
  animateBy = 'words',
  threshold = 0.1,
  stepDuration = 0.35
}) => {
  const elements = animateBy === 'words' ? text.split(' ') : text.split('');
  const [inView, setInView] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return (
    <span ref={ref} className={`blur-text ${className} flex flex-wrap`}>
      {elements.map((segment, index) => {
        const spanTransition: Transition = {
          duration: stepDuration * 2,
          times: TIMES,
          delay: (index * delay) / 1000,
          ease: 'linear'
        };

        return (
          <motion.span
            key={index}
            initial={FROM}
            animate={inView ? KEYFRAMES : FROM}
            transition={spanTransition}
            style={{
              display: 'inline-block',
              willChange: 'transform, filter, opacity'
            }}
          >
            {segment === ' ' ? '\u00A0' : segment}
            {animateBy === 'words' && index < elements.length - 1 && '\u00A0'}
          </motion.span>
        );
      })}
    </span>
  );
};

export default BlurText;
